/**
 * AcupunctureConsent Component
 *
 * Presentational component for the Acupuncture/Traditional Chinese Medicine
 * consent form. Displays the full consent text, health condition checkboxes,
 * and signature canvas.
 *
 * Props:
 *   - checked: bool - Whether the acupuncture section is enabled
 *   - onChange: func(fieldName, value) - Callback for checkbox changes
 *   - answers: obj - Current values for all acupuncture-related fields
 *   - signatureRef: ref - React ref for the signature canvas
 *   - onClearSignature: func - Callback to clear the signature
 *   - readOnly: bool (optional) - If true, disable interaction (for historical view)
 *   - version: obj (optional) - Version metadata for historical rendering
 */

import React from "react";
import {
  Typography,
  FormControlLabel,
  Checkbox,
  Button,
  Card,
  CardContent,
} from "@material-ui/core";
import SignatureCanvas from "react-signature-canvas";

/**
 * Default props shape
 */
const defaultProps = {
  checked: false,
  onChange: () => {},
  answers: {},
  signatureRef: null,
  onClearSignature: () => {},
  readOnly: false,
  version: null,
};

/**
 * AcupunctureConsent - Acupuncture/Traditional Chinese Medicine Service Consent Form
 */
function AcupunctureConsent({
  checked,
  onChange,
  answers,
  signatureRef,
  onClearSignature,
  readOnly,
  version,
}) {
  if (!checked) {
    return null;
  }

  return (
    <div>
      {/* Historical disclaimer if viewing old version */}
      {readOnly && version && (
        <Card style={{ marginBottom: 16, backgroundColor: "#fffacd" }}>
          <CardContent>
            <Typography variant="body2" style={{ color: "#856404" }}>
              <b>Historical View:</b> This form was submitted on{" "}
              {version.submittedAt?.split("T")[0]} under consent version{" "}
              {version.versionId} and is displayed for reference only.
            </Typography>
          </CardContent>
        </Card>
      )}

      <Typography variant="h4" style={{ textAlign: "left" }}>
        Acupuncture/Traditional Chinese Medicine Service Consent Form
      </Typography>
      <br />
      <br />
      <Typography variant="body1" style={{ textAlign: "left" }}>
        Please carefully read this information, and if you have any questions,
        please ask our practitioner(s). <br />
        <br />
        <b>Privacy Policy</b> <br />
        The information received and collected about our clients/patients from
        their visit(s) to Appleby Wellness Centre is private and confidential.
        It is only used and viewed by the healthcare professionals and staff
        employed or contracted by our centre, unless, in the best interest of
        the client/patient, a practitioner determines that there is a need to
        communicate with another person or healthcare professional outside of
        Appleby Wellness Centre (also, we will not give, share, sell, or
        transfer any personal information to a third party unless required by
        law). Appleby Wellness Centre communicates with our clients by mail,
        email and or other electronic channels. On occasion, we may use
        client/patient information to conduct clinical studies to help us
        improve upon services provided. Appleby Wellness Centre may allow
        interns and students to get access to clinical files to clinical files
        for observations and clinical placements purposes.
        <br />
        <br />
        <b>Statement of Consent to Treatment</b>
        <br />
        I, or the person listed below, have discussed with my Traditional
        Chinese Medicine Practitioner or Acupuncturist the specifics of my
        assessment or treatment and understand the nature, risks and reasons for
        this procedure. I voluntarily consent to Traditional Chinese
        Medicine/Acupuncture and understand that I may withdraw my consent and
        halt my participation at any time.
        <br />
        <br />
        <b>1. Nature and Techniques:</b> I understand that some of the
        techniques used under the scope of Traditional Chinese Medicine include
        the use of sterile, single-use needles to penetrate the skin. Additional
        treatment methods can include, but are not limited to: acupuncture,
        acupressure, the electrical stimulation of needles, cupping or
        moxibustion, gua sha, and acupressure including Tuina(Chinese massage)
        and shiatsu massage. Before any of these procedures are performed, may
        practitioner will discuss my treatment options and only proceed with my
        consent.
        <br />
        <br />
        <b>2. Risks & Symptoms:</b> I understand that the risks and symptoms of
        treatments, which can include, but are not limited to: slight pain,
        light headaches, dizziness or nausea, soreness, bruising, bleeding or
        discolouration of the skin, and the possibility of other unforeseen
        risks, such as spontaneous miscarriage, nerve damage and organ puncture,
        and infection. For moxibustion and fire-cupping practices may have
        potential risks (e.g. burns, blisters and/or scarring etc.) and possible
        side effects (e.g. local skin discoloration, skin tenderness, allergic
        reactions to lotions and/or cleaning agent residues etc.) I freely
        accept the risks involved with my procedure.
        <br />
        <br />
        <b>3. Health Issues:</b> I will inform my practitioner if I currently
        have or develop any major heath issues, if I suffer from any type of
        major bleeding disorder, if I am or believe to be pregnant or if I use a
        pacemaker or I have metal in my body. I understand that I must let my
        practitioner know if I am carrying, or believe to have any infectious
        agents, including but at not limited to HIV/AIDS, TB and Hepatitis. In
        some cases where cross-infection is high, my practitioner may withhold
        treatment.
        <br />
      </Typography>
      <br />
      <br />
      <Typography variant="body1" style={{ textAlign: "left" }}>
        Please check all that may apply
      </Typography>
      <br />
      <FormControlLabel
        control={
          <Checkbox
            checked={answers.consentBleedEasily || false}
            onChange={(e) => onChange("consentBleedEasily", e.target.checked)}
            color="primary"
            name="consentBleedEasily"
            disabled={readOnly}
          />
        }
        label="I bleed or bruise easily or have a major bleeding disorder"
      />
      <br />
      <FormControlLabel
        control={
          <Checkbox
            checked={answers.consentPregnant || false}
            onChange={(e) => onChange("consentPregnant", e.target.checked)}
            color="primary"
            name="consentPregnant"
            disabled={readOnly}
          />
        }
        label="I am pregnant or plan on becoming pregnant"
      />
      <br />
      <FormControlLabel
        control={
          <Checkbox
            checked={answers.consentHIV || false}
            onChange={(e) => onChange("consentHIV", e.target.checked)}
            color="primary"
            name="consentHIV"
            disabled={readOnly}
          />
        }
        label="I know/suspect I have HIV/AIDS, Hepatitis, TB, others"
      />
      <br />
      <FormControlLabel
        control={
          <Checkbox
            checked={answers.consentPacemaker || false}
            onChange={(e) => onChange("consentPacemaker", e.target.checked)}
            color="primary"
            name="consentPacemaker"
            disabled={readOnly}
          />
        }
        label="I use a pacemaker"
      />
      <br />
      <FormControlLabel
        control={
          <Checkbox
            checked={answers.consentMetal || false}
            onChange={(e) => onChange("consentMetal", e.target.checked)}
            color="primary"
            name="consentMetal"
            disabled={readOnly}
          />
        }
        label="I have metal in my body"
      />
      <br />
      <br />
      <Typography variant="body1" style={{ textAlign: "left" }}>
        <b>4. Results:</b> I understand that there are no guarantees for the
        results of my treatments. Traditional Chinese Medicine/Acupuncture does
        not often provide an instant cure. The length of my treatment depends on
        the severity of my condition. In some cases my symptoms my temporarily
        worsen before they begin to improve.
        <br />
        <br />
        <b>5. Fees:</b> I understand that the fees charged for my treatment are
        not covered under OHIP and must be covered in full by myself or through
        third party insurance. I am responsible for the full and prompt payment
        after services have been rendered.
        <br />
        <br />
        <b>6. Appointment Policy :</b> All the treatments are offered by
        appointment.{" "}
        <b>
          <u>A 24 hour notice</u> for cancelled or rescheduled appointments is
          required in order to avoid the $100.00 cancellation fee.
        </b>{" "}
        Occasionally, if you are going to be more than 15 minutes late, please
        call to confirm availability.
        <br />
        <br />
        <b>7. Informed Consent:</b> I have discussed the content of this form
        with my practitioner. I acknowledge that I have asked any questions I
        may have and received answers I understand. By signing this form, I give
        my informed consent for Traditional Chinese Medicine/Acupuncture
        Treatments.
        <br />
        <br />
        <br />
        {/* New privacy policy: 07/2025 */}
        <b>Consent to Collect Use, and Disclose Personal Health Information</b>
        <br />
        <br />
        I, or my substitute decision-maker, consent to for Appleby Wellness
        Clinic to collect, use and disclose my personal health information for
        the purpose of providing traditional Chinese medicine or acupuncture to
        me and for the related purposes set out in Appleby Wellness Clinic's
        Written Privacy Statement. The personal health information that may be
        collected, used or disclosed by the Clinic may include the following,
        among other things:
        <br />
        <ul>
          <li>my birth date and contact information</li>
          <li>my health history and family health history</li>
          <li>my health status</li>
          <li>
            the health care I receive (including identifying my health care
            provider(s));
          </li>
          <li>my health number</li>
          <li>the identification of my substitute decision-maker, if any</li>
          <li>insurance or billing information relating to health care</li>
        </ul>
        I understand that there may be situations in which practitioners at
        Appleby Wellness Clinic will have to collect, use or disclose personal
        health information without my consent, but that they will only do this
        if permitted by law.
        <br />
        <br />
        <b>How My Information Will Be Used</b>
        <br />
        I understand that my personal health information may be collected, used
        or disclosed for the following reasons:
        <br />
        <ul>
          <li>
            To provide me with traditional Chinese medicine or acupuncture
            services
          </li>
          <li>To obtain payment for services provided</li>
          <li>
            To assist insurance companies with insurance claims verification
          </li>
          <li>To seek advice for potential treatment options</li>
          <li>To provide or arrange health care in cases of emergencies</li>
          <li>To fulfill any obligations as mandated by law</li>
        </ul>
        <b>Patient Access to Information</b>
        <br />
        I understand that my personal health information is available to me for
        my review except in limited circumstances as permitted by law. I also
        understand that I can ask to have my personal health information
        corrected if I believe there is a mistake in the records, with some
        exceptions.
        <br />
        <br />
        <b>Acknowledgment</b>
        <br />I allow Appleby Wellness Clinic to collect, use and disclose my
        personal health information as outlined above. I understand that I can
        access my personal health information with some limited exceptions. I
        understand that I am not required to sign this form and that I can
        withdraw my consent at any time by contacting Appleby Wellness Clinic,
        but it may directly affect the services I can receive. My personal
        health information may still be collected, used or disclosed if
        permitted by law.
      </Typography>
      <br />
      <br />
      <Typography variant="body1" style={{ textAlign: "left" }}>
        Please sign in the box below.
      </Typography>
      <div
        style={{
          borderStyle: "dotted",
          borderWidth: 2,
          borderColor: "#D3D3D3",
          width: 530,
          height: 240,
          borderRadius: 4,
        }}
      >
        <SignatureCanvas
          penColor="black"
          ref={signatureRef}
          canvasProps={{
            width: 530,
            height: 240,
            className: "consentAcupunctureSig",
          }}
          disabled={readOnly}
        />
      </div>
      <br />
      {!readOnly && <Button onClick={onClearSignature}>Clear</Button>}
      <br />
      <br />
      <br />
    </div>
  );
}

AcupunctureConsent.defaultProps = defaultProps;

export default AcupunctureConsent;
